/**
 * TanStack Query hooks backing the admin merchants feature.
 *
 * `GET /api/v1/admin/merchants` is a paginated list endpoint that exists on
 * the backend (see AdminMerchantController#listAllMerchants) but isn't part
 * of the generated OpenAPI spec yet. We hit it via a direct fetch with the
 * stored access token; the rest of the calls go through the typed `api`
 * client. TODO: drop `fetchAdminMerchantsList` once the OpenAPI is updated.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import {
  fetchAuthorized,
  type MerchantsListFilters,
} from '@favelafood/shared/api/adminMerchants';

// `useMerchants`/`fetchAdminMerchantsList`/`MerchantsListFilters` moved to
// `@favelafood/shared/api/adminMerchants` (shared with the comerciante store
// pickers). Re-exported so existing `@/features/merchants/hooks` consumers keep
// resolving unchanged.
export { useMerchants, fetchAdminMerchantsList, type MerchantsListFilters } from '@favelafood/shared/api/adminMerchants';

type MerchantResponse = components['schemas']['MerchantResponse'];
type MerchantStatusEnum = components['schemas']['MerchantStatusEnum'];
type PageResponseOrder = components['schemas']['PageResponseOrderResponse'];
type PageResponseReview = components['schemas']['PageResponseReviewResponse'];

export const merchantsKeys = {
  all: ['merchants'] as const,
  lists: () => [...merchantsKeys.all, 'list'] as const,
  list: (filters: MerchantsListFilters) => [...merchantsKeys.lists(), filters] as const,
  detail: (id: string) => [...merchantsKeys.all, 'detail', id] as const,
  orders: (id: string) => [...merchantsKeys.all, 'orders', id] as const,
  reviews: (id: string) => [...merchantsKeys.all, 'reviews', id] as const,
};

/**
 * Detail fetch — FAV-144: reads the admin endpoint `GET /api/v1/admin/merchants/{id}`,
 * which returns `MerchantResponse` for ANY status (PENDING/SUSPENDED/REJECTED), unlike
 * the public storefront `/public/merchants/{id}` (APPROVED-only). Uses the shared
 * `fetchAuthorized` helper (same as the list) since the admin-by-id path is not
 * yet in the generated OpenAPI client types.
 */
const fetchMerchantDetail = (id: string): Promise<MerchantResponse> =>
  fetchAuthorized<MerchantResponse>(`/api/v1/admin/merchants/${encodeURIComponent(id)}`);

const unwrap = <T,>(data: T | undefined, error: unknown): T => {
  if (error) throw error;
  if (data === undefined) throw new Error('Resposta vazia do servidor');
  return data;
};

export const useMerchant = (
  id: string,
): UseQueryResult<MerchantResponse> =>
  useQuery({
    queryKey: merchantsKeys.detail(id),
    queryFn: () => fetchMerchantDetail(id),
    enabled: Boolean(id),
  });

export const useMerchantOrders = (id: string): UseQueryResult<PageResponseOrder> =>
  useQuery({
    queryKey: merchantsKeys.orders(id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/merchants/{id}/orders', {
        params: { path: { id }, query: { page: 0, size: 50 } },
      });
      return unwrap(data, error);
    },
    enabled: Boolean(id),
  });

export const useMerchantReviews = (id: string): UseQueryResult<PageResponseReview> =>
  useQuery({
    queryKey: merchantsKeys.reviews(id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/merchants/{id}/reviews', {
        params: { path: { id }, query: { page: 0, size: 50 } },
      });
      return unwrap(data, error);
    },
    enabled: Boolean(id),
  });

const invalidateMerchant = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
): void => {
  void queryClient.invalidateQueries({ queryKey: merchantsKeys.detail(id) });
  void queryClient.invalidateQueries({ queryKey: merchantsKeys.lists() });
};

export type UpdateMerchantRequest = components['schemas']['UpdateMerchantRequest'];

export interface UpdateMerchantVars {
  id: string;
  body: UpdateMerchantRequest;
}

export const useUpdateMerchant = (): UseMutationResult<
  MerchantResponse,
  Error,
  UpdateMerchantVars
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: UpdateMerchantVars) => {
      const { data, error } = await api.PUT('/api/v1/merchants/{id}', {
        params: { path: { id } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateMerchant(queryClient, vars.id),
  });
};

export interface UpdateMerchantStatusVars {
  id: string;
  status: MerchantStatusEnum;
}

export const useUpdateMerchantStatus = (): UseMutationResult<
  MerchantResponse,
  Error,
  UpdateMerchantStatusVars
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: UpdateMerchantStatusVars) => {
      const { data, error } = await api.PATCH('/api/v1/admin/merchants/{id}/status', {
        params: { path: { id } },
        body: { status },
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateMerchant(queryClient, vars.id),
  });
};

export interface ToggleAcceptingVars {
  id: string;
  accepting: boolean;
}

export const useToggleAcceptingOrders = (): UseMutationResult<
  MerchantResponse,
  Error,
  ToggleAcceptingVars
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accepting }: ToggleAcceptingVars) => {
      const { data, error } = await api.PATCH(
        '/api/v1/admin/merchants/{id}/accepting-orders',
        {
          params: { path: { id } },
          body: { accepting },
        },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => invalidateMerchant(queryClient, vars.id),
  });
};
